import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { SidenavService } from '../../services/sidenav.service';
import { SnackbarService } from '../../services/snackbar.service';
import {
  WhatsAppMessageStatus,
  WhatsAppService,
  WhatsAppStatus,
} from '../../services/whatsapp.service';

@Component({
  selector: 'app-whatsapp-status',
  templateUrl: './whatsapp-status.component.html',
  styleUrls: ['./whatsapp-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WhatsAppStatusComponent implements OnInit {
  @BlockUI() blockUI!: NgBlockUI;

  sidenav: MatSidenav;
  status: WhatsAppStatus | null = null;
  messages: WhatsAppMessageStatus[] = [];
  selectedMessage: WhatsAppMessageStatus | null = null;

  constructor(
    private sidenavService: SidenavService,
    private whatsappService: WhatsAppService,
    private snackbar: SnackbarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
  }

  async ngOnInit(): Promise<void> {
    await this.refresh();
  }

  toggleMenu(): void {
    this.sidenavService.toggle();
  }

  goBack(): void {
    this.router.navigate(['/admin']);
  }

  async refresh(): Promise<void> {
    this.blockUI.start();
    try {
      const [status, messages] = await Promise.all([
        this.whatsappService.getStatus(),
        this.whatsappService.getMessages(),
      ]);
      this.status = status;
      this.messages = messages;
      if (this.selectedMessage) {
        this.selectedMessage =
          messages.find((message) => message.messageId === this.selectedMessage?.messageId) ||
          this.selectedMessage;
      }
    } catch {
      this.snackbar.error('No se pudo obtener el estado de WhatsApp.');
    } finally {
      this.blockUI.stop();
      this.cdr.markForCheck();
    }
  }

  async viewMessage(messageId: string): Promise<void> {
    this.blockUI.start();
    try {
      this.selectedMessage = await this.whatsappService.getMessage(messageId);
    } catch (error: any) {
      this.snackbar.error(
        error?.message || 'No se pudo obtener el detalle del mensaje.',
      );
    } finally {
      this.blockUI.stop();
      this.cdr.markForCheck();
    }
  }

  async start(): Promise<void> {
    this.blockUI.start();
    try {
      this.status = await this.whatsappService.start();
      this.snackbar.success('Arranque de WhatsApp solicitado.');
    } catch (error: any) {
      this.snackbar.error(error?.message || 'No se pudo arrancar WhatsApp.');
    } finally {
      this.blockUI.stop();
      this.cdr.markForCheck();
    }
  }

  getStatusTitle(): string {
    if (!this.status?.enabled) {
      return 'WhatsApp desactivado';
    }

    if (this.status.connected) {
      return 'WhatsApp conectado';
    }

    if (this.status.hasQr) {
      return 'Esperando escaneo de QR';
    }

    return 'WhatsApp desconectado';
  }

  getStatusDescription(): string {
    if (!this.status?.enabled) {
      return 'Activa WHATSAPP_ENABLED en el backend para usar esta integración.';
    }

    if (this.status.connected) {
      return 'La instancia está lista para enviar mensajes.';
    }

    if (this.status.stoppedReason) {
      return `La conexión se detuvo por: ${this.status.stoppedReason}.`;
    }

    return 'La instancia no está conectada ahora mismo.';
  }

  getMessageStatusLabel(status: WhatsAppMessageStatus['status']): string {
    switch (status) {
      case 'server_ack':
        return 'Aceptado';
      case 'delivered':
        return 'Entregado';
      case 'read':
        return 'Leído';
      case 'played':
        return 'Reproducido';
      case 'error':
        return 'Error';
      case 'pending':
        return 'Pendiente';
      default:
        return 'Desconocido';
    }
  }
}
